/**
 * Auth Store — derived state only.
 *
 * AXF-1013: the portal keeps no session of its own. There is no stored token, nothing written to
 * localStorage, and no way to authenticate from here — the shared `axflow_session` cookie is the
 * single source of truth and `useCurrentUser` reads it through the session probe on every mount.
 * What remains is a place to hang the answer so the rest of the app can read it synchronously.
 */
import { proxy } from 'valtio'
import type { User } from '@domain/entities'

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isInitialized: boolean
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isInitialized: false,
}

export const authStore = proxy<AuthState>(initialState)

export const authActions = {
  setUser: (user: User | null) => {
    authStore.user = user
    authStore.isAuthenticated = !!user
  },

  logout: () => {
    authStore.user = null
    authStore.isAuthenticated = false
  },

  /**
   * AXF-1013: there is nothing to restore. The portal keeps no session of its own — the cookie is
   * the single source of truth and `useCurrentUser` reads it on mount. This stays only so the
   * `isInitialized` flag keeps its meaning for the guard's loading branch.
   *
   * It deliberately does NOT read the old `auth_token` / `admin_user` keys, and deliberately does
   * NOT clear them: a bootstrap that preferred them would work on the team's browsers and fail on
   * everyone else's (spec R5), and the only cost of leaving them is a few bytes in a browser.
   */
  initialize: () => {
    authStore.isInitialized = true
  },
}

export const authSelectors = {
  isAuthenticated: () => authStore.isAuthenticated,
  currentUser: () => authStore.user,
  isDevAdmin: () => authStore.user?.isDevAdmin ?? false,
  isSuperAdmin: () => authStore.user?.isSuperAdmin ?? false,
}
